import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { getDataSource, executeQuery } from '../../../../../services/datasourceService';
import { QueryResult } from '../../../../../types/dataSource';
import { LoadingSpinner } from '../../../../common';
import { AdminCard } from '../../../ui';

interface DataSourceViewerProps {
  id?: string;
}

interface CellModalProps {
  isOpen: boolean;
  onClose: () => void;
  content: string;
  header: string;
}

const ROWS_PER_PAGE = 10;
const MAX_CELL_LENGTH = 100;

const CellModal: React.FC<CellModalProps> = ({ isOpen, onClose, content, header }) => {
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-40" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-[#2a2a2a] rounded-2xl z-50 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-[#333333]">
          <h3 className="text-white font-medium">{header}</h3>
          <button
            onClick={onClose}
            className="p-2 text-[#888888] hover:text-white hover:bg-[#333333] rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          <pre className="text-[#cccccc] text-sm whitespace-pre-wrap font-mono bg-[#1a1a1a] p-4 rounded-lg">
            {content}
          </pre>
        </div>
      </div>
    </>
  );
};

export const DataSourceViewer: React.FC<DataSourceViewerProps> = ({ id }) => {
  const [query, setQuery] = useState<string>('');
  const [results, setResults] = useState<QueryResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    content: string;
    header: string;
  }>({
    isOpen: false,
    content: '',
    header: ''
  });

  const refreshResults = useCallback(async (newOffset?: number) => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const currentOffset = newOffset ?? 0;
      const response = await executeQuery(Number(id), ROWS_PER_PAGE, currentOffset);

      if (newOffset) {
        setResults(prev => [...prev, ...response.results]);
      } else {
        setResults(response.results);
      }

      setTotal(response.total);
      setOffset(currentOffset);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Falha ao executar consulta');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadDataSource = useCallback(async () => {
    try {
      const response = await getDataSource(Number(id));
      if (response.success && response.data?.rag_config?.document_search?.base_sql_query) {
        setQuery(response.data.rag_config.document_search.base_sql_query);
        setOffset(0);
        await refreshResults(0);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar fonte de dados');
    }
  }, [id, refreshResults]);

  useEffect(() => {
    if (id) {
      loadDataSource();
    }
  }, [id, loadDataSource]);

  const truncateText = (text: string) => {
    if (!text) return '';
    const str = text.toString();
    if (str.length <= MAX_CELL_LENGTH) return str;
    return `${str.substring(0, MAX_CELL_LENGTH)}...`;
  };

  const handleLoadMore = () => {
    const newOffset = offset + ROWS_PER_PAGE;
    refreshResults(newOffset);
  };

  const handleRefresh = () => {
    setOffset(0);
    refreshResults(0);
  };

  const handleCellClick = (content: string, header: string) => {
    if (content.length > MAX_CELL_LENGTH) {
      setModalState({
        isOpen: true,
        content,
        header
      });
    }
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  const renderTableHeaders = () => {
    if (results.length === 0) return null;
    const headers = Object.keys(results[0]);

    return (
      <tr>
        {headers.map((header) => (
          <th
            key={header}
            title={header}
            className="px-4 py-3 text-left text-xs font-semibold text-[#cccccc] uppercase tracking-wider bg-[#1a1a1a] border-b border-[#333333] whitespace-nowrap"
          >
            {truncateText(header)}
          </th>
        ))}
      </tr>
    );
  };

  const renderTableRows = () => {
    return results.map((row, index) => (
      <tr key={index} className="hover:bg-[#1a1a1a]/50 transition-colors">
        {Object.entries(row).map(([key, value], colIndex) => {
          const stringValue = value?.toString() || '';
          const isTruncated = stringValue.length > MAX_CELL_LENGTH;
          return (
            <td
              key={colIndex}
              title={isTruncated ? 'Clique para ver conteúdo completo' : stringValue}
              onClick={() => handleCellClick(stringValue, key)}
              className={`
                px-4 py-3 text-sm text-[#cccccc] border-b border-[#333333]
                ${isTruncated ? 'cursor-pointer hover:text-[#F4CC04]' : ''}
              `}
            >
              {truncateText(stringValue)}
            </td>
          );
        })}
      </tr>
    ));
  };

  return (
    <AdminCard
      title="Visualizador de Dados"
      headerAction={
        <button
          onClick={handleRefresh}
          disabled={loading || !id}
          className="
            flex items-center gap-2 px-3 py-2
            bg-[#333333] hover:bg-[#444444]
            text-white text-sm font-medium
            rounded-lg transition-colors
            disabled:opacity-50 disabled:cursor-not-allowed
          "
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Atualizando...' : 'Atualizar'}
        </button>
      }
    >
      {/* Query Display */}
      {query && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-[#cccccc] mb-2">Query SQL</h3>
          <pre className="p-4 bg-[#1a1a1a] rounded-lg text-sm text-[#888888] font-mono overflow-x-auto border border-[#333333]">
            {query}
          </pre>
        </div>
      )}

      {/* Results */}
      <div>
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm mb-4">
            {error}
          </div>
        )}

        {loading && results.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="large" />
          </div>
        ) : results.length > 0 ? (
          <>
            <div className="text-sm text-[#888888] mb-4">
              Mostrando <span className="text-white font-medium">{results.length}</span> de{' '}
              <span className="text-white font-medium">{total}</span> resultados
            </div>

            <div className="overflow-x-auto rounded-lg border border-[#333333]">
              <table className="w-full">
                <thead>{renderTableHeaders()}</thead>
                <tbody>{renderTableRows()}</tbody>
              </table>
            </div>

            {results.length < total && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="
                    px-6 py-3
                    bg-[#333333] hover:bg-[#444444]
                    text-white font-medium
                    rounded-lg transition-colors
                    disabled:opacity-50 disabled:cursor-not-allowed
                  "
                >
                  {loading ? 'Carregando...' : 'Carregar Mais Resultados'}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-[#888888] text-sm">
              {error ? 'Nenhum resultado disponível devido a erro' : 'Nenhum resultado disponível'}
            </p>
            {!error && (
              <p className="text-[#666666] text-xs mt-1">
                {query ? 'A query não retornou resultados' : 'Nenhuma query disponível'}
              </p>
            )}
          </div>
        )}
      </div>

      <CellModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        content={modalState.content}
        header={modalState.header}
      />
    </AdminCard>
  );
};

export default DataSourceViewer;
